import { ActivityEntityName, EntityQueueType, MetadataEntityData, MetadataEntityFieldData, MetadataEntityFieldDomain, MetadataEntityFieldEditorType, MetadataEntityFieldType, MetadataEntityRecordScope } from '@jms/model';

// Pure field-option helpers behind the queue wizard's column and card pickers. Shared by the inbox
// store and the settings edit dialog; nothing here touches Angular or the store.

export interface QueueFieldOption {
  path: string;
  name: string;
  displayName: string;
  qualifiedDisplayName: string;
  group: string;
  icon: string | null;
  source: 'entity' | 'flag';
  sortable: boolean;
}

function isStageQueueTarget(entity: ActivityEntityName | undefined): boolean {

  return entity === ActivityEntityName.JobStages || entity === ActivityEntityName.WorkOrder;
}

function isCustomField(f: MetadataEntityFieldData): boolean {

  const type = Array.isArray(f.type) ? f.type[0] : f.type;
  return type === MetadataEntityFieldType.CustomField;
}

function isSecondaryField(f: MetadataEntityFieldData): boolean {

  // Scope is authoritative for secondary custom fields such as WorkOrder fields on Job metadata.
  if (f.scope === MetadataEntityRecordScope.Secondary) return true;
  // Primary custom fields carry a dotted path ('customFields.<id>') without being secondary.
  if (isCustomField(f)) return false;
  return (f.path ?? '').includes('.');
}

function isNoteField(f: MetadataEntityFieldData): boolean {

  return f.editor === MetadataEntityFieldEditorType.TextArea || f.editor === MetadataEntityFieldEditorType.RichText || f.editor === MetadataEntityFieldEditorType.MergeInput;
}

export function resolveColumnUiFields(metadata: MetadataEntityData, includeSecondary = false): MetadataEntityFieldData[] {

  if (!metadata) return [];
  const domain = new MetadataEntityFieldDomain(metadata);
  // Virtual fields are filter-only — they have no stored document value, so offering them as
  // columns renders blank cells.
  return Object.values(domain.uiFields).filter(x => !x.hidden && !x.virtual && (includeSecondary || !isSecondaryField(x)));
}

function resolveCardUiFields(metadata: MetadataEntityData, includeSecondary = false): MetadataEntityFieldData[] {

  if (!metadata) return [];
  const domain = new MetadataEntityFieldDomain(metadata);
  return Object.values(domain.uiFields).filter(x => !x.hidden && !x.virtual && (includeSecondary || !isSecondaryField(x)) && !isNoteField(x));
}

export function toQueueFieldOptions(fields: MetadataEntityFieldData[], source: 'entity' | 'flag', group: string, prefixPath = false): QueueFieldOption[] {

  return fields.map(f => {
    const rawPath = f.path ?? f.name;
    const path = prefixPath ? `${source}.${rawPath}` : rawPath;
    const baseLabel = f.displayName ?? f.name;
    return { path, name: f.name, displayName: baseLabel, qualifiedDisplayName: `${group} · ${baseLabel}`, group, icon: f.icon ?? null, source, sortable: f.sortable ?? false };
  });
}

export function toStageQueueFieldOptions(fields: MetadataEntityFieldData[], prefixPath = true): QueueFieldOption[] {

  return fields.map(f => {
    const rawPath = f.path ?? f.name;
    const path = prefixPath ? `entity.${rawPath}` : rawPath;
    const group = rawPath.startsWith('workOrders.') ? 'Stage' : 'Job';
    const baseLabel = f.displayName ?? f.name;
    return { path, name: f.name, displayName: baseLabel, qualifiedDisplayName: `${group} · ${baseLabel}`, group, icon: f.icon ?? null, source: 'entity' as const, sortable: f.sortable ?? false };
  });
}

export interface QueueCardFieldOptions {
  identifier: QueueFieldOption[];
  title: QueueFieldOption[];
  description: QueueFieldOption[];
  status: QueueFieldOption[];
  body: QueueFieldOption[];
  footer: QueueFieldOption[];
}

export function resolveQueueCardFieldOptions(fields: QueueFieldOption[], metadataMap: Record<string, MetadataEntityData>, type: EntityQueueType, targetEntity: ActivityEntityName): QueueCardFieldOptions {

  const card: QueueCardFieldOptions = { identifier: [], title: [], description: [], status: [], body: [], footer: [] };

  const targetMetadata = isStageQueueTarget(targetEntity) ? metadataMap[ActivityEntityName.Job] : metadataMap[targetEntity];
  const allMetadataFields = type === EntityQueueType.Flag
    ? [...resolveCardUiFields(metadataMap[ActivityEntityName.Flag] ?? null), ...resolveCardUiFields(targetMetadata ?? null, isStageQueueTarget(targetEntity))]
    : resolveCardUiFields(targetMetadata ?? null, isStageQueueTarget(targetEntity));

  const fieldMap = new Map(allMetadataFields.map(f => [f.path ?? f.name, f]));

  for (const opt of fields) {

    // Strip source prefix (flag./entity.) to match raw metadata paths
    const rawPath = opt.path.replace(/^(flag|entity)\./, '');
    const meta = fieldMap.get(rawPath);
    if (!meta) { card.body.push(opt); continue; }

    const rawType = meta.type;
    const isArray = Array.isArray(rawType);
    const fieldType = isArray ? rawType[0] : rawType;
    const visualType = meta.visualType;

    if (meta.identifier) card.identifier.push(opt);
    if ((visualType === 'status' || visualType === 'color') && !isArray) card.status.push(opt);
    if (visualType === 'color') card.footer.push(opt);

    switch (fieldType) {
      case MetadataEntityFieldType.String:
      case MetadataEntityFieldType.StringAlphaNumeric:
      case MetadataEntityFieldType.StringAlpha:
      case MetadataEntityFieldType.Phone:
      case MetadataEntityFieldType.Email:
      case MetadataEntityFieldType.Url:
      case MetadataEntityFieldType.Time:
      case MetadataEntityFieldType.Duration:
      case MetadataEntityFieldType.Number:
      case MetadataEntityFieldType.Percentage:
      case MetadataEntityFieldType.Currency:
      case MetadataEntityFieldType.Date:
      case MetadataEntityFieldType.DateTime:
        card.title.push(opt);
        card.body.push(opt);
        break;
      case MetadataEntityFieldType.Site:
      case MetadataEntityFieldType.Address:
        card.body.push(opt);
        break;
      case MetadataEntityFieldType.Lookup:
      case MetadataEntityFieldType.Tag:
      case MetadataEntityFieldType.Enum:
        if (!isArray) { card.title.push(opt); card.body.push(opt); }
        break;
    }

    if (!isArray && (fieldType === MetadataEntityFieldType.String || fieldType === MetadataEntityFieldType.StringAlphaNumeric || fieldType === MetadataEntityFieldType.StringAlpha || fieldType === MetadataEntityFieldType.Address || fieldType === MetadataEntityFieldType.Site || fieldType === MetadataEntityFieldType.Lookup)) {
      card.description.push(opt);
    }
  }

  return card;
}

/** Disambiguates display names for entity-source fields that collide with flag-source fields. */
export function dedupeLabels(flagFields: QueueFieldOption[], entityFields: QueueFieldOption[]): QueueFieldOption[] {

  const flagLabels = new Set(flagFields.map(f => f.displayName));
  for (const f of entityFields) {
    if (flagLabels.has(f.displayName)) f.displayName = f.qualifiedDisplayName;
  }
  return [...flagFields, ...entityFields];
}
